import { X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { CELEBRATION_COPY } from "@/lib/milestones";

/**
 * Floating banner that appears when the admin opens the dashboard and the
 * subscriber count just crossed a new milestone since their last visit.
 *
 * Pairs with `useMilestoneCelebration` (which also fires the confetti burst).
 */
const MilestoneCelebrationBanner = ({ milestone, onDismiss }) => (
  <AnimatePresence>
    {milestone && (
      <motion.div
        initial={{ opacity: 0, y: -32, scale: 0.96 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: -24, scale: 0.96 }}
        transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
        className="fixed top-24 left-1/2 -translate-x-1/2 z-[60] w-[min(560px,calc(100vw-2rem))]"
        data-testid="milestone-celebration-banner"
        role="status"
        aria-live="polite"
      >
        <div className="relative bg-charcoal text-ivory rounded-3xl border border-terracotta/40 shadow-2xl shadow-charcoal/40 grain p-7 md:p-8 overflow-hidden">
          <div className="absolute -top-12 -right-12 w-40 h-40 rounded-full bg-terracotta/30 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-12 -left-12 w-40 h-40 rounded-full bg-sage/25 blur-3xl pointer-events-none" />

          <button
            type="button"
            onClick={onDismiss}
            aria-label="Cerrar celebración"
            data-testid="milestone-banner-dismiss"
            className="absolute top-4 right-4 text-ivory/55 hover:text-ivory transition-colors"
          >
            <X size={18} />
          </button>

          <div className="relative">
            <p className="text-[10px] tracking-[0.32em] uppercase text-terracotta mb-3">¡Nuevo hito!</p>
            <p className="text-5xl md:text-6xl mb-4" aria-hidden="true">{milestone.emoji}</p>
            <h2 className="font-serif text-3xl md:text-4xl leading-[1.1]">
              <span className="text-terracotta italic">{milestone.count}</span> suscriptoras
            </h2>
            <p className="mt-1 text-[12px] tracking-[0.3em] uppercase text-ivory/55">
              {milestone.label}
            </p>
            <p className="mt-5 text-ivory/85 leading-relaxed text-[15px]">
              {CELEBRATION_COPY[milestone.count]}
            </p>
            <button
              type="button"
              onClick={onDismiss}
              data-testid="milestone-banner-continue"
              className="mt-6 inline-flex items-center gap-2 bg-terracotta text-ivory rounded-full px-6 py-3 text-sm font-medium hover:bg-terracotta/85 transition-colors"
            >
              Seguir publicando
            </button>
          </div>
        </div>
      </motion.div>
    )}
  </AnimatePresence>
);

export default MilestoneCelebrationBanner;
